import { Link, useParams } from '@tanstack/react-router';
import { Ban, Flag } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState, FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';

import { ReadOnlyBanner } from '@/shared/store/read-only-banner';
import { displayTimeZone, useStore } from '@/shared/store/store-context';
import { useStoreEditable } from '@/shared/store/use-store-editable';

import { AuctionBidsCard } from '../components/auction-bids-card';
import {
  AuctionLotCard,
  AuctionOutcomeCard,
  AuctionSettingsCard,
} from '../components/auction-cards';
import { AuctionFormDialog } from '../components/auction-form-dialog';
import { AuctionOutcomeBadge, AuctionStatusBadge } from '../components/auction-status-badge';
import { AuctionSummary } from '../components/auction-summary';
import { EndAuctionDialog } from '../components/end-auction-dialog';
import { ReasonDialog } from '../components/reason-dialog';
import { SecondChanceCard } from '../components/second-chance-card';
import { useAuction } from '../hooks/use-auction';
import { useCancelAuction } from '../hooks/use-auction-mutations';
import { canCancel, isEditable } from '../schemas/auction-rules';

const ROUTE = '/_authed/_active/s/$storeId/auctions/$auctionId' as const;

function AuctionDetail() {
  const { t } = useTranslation('auctions');
  const format = useFormat();
  const store = useStore();
  const { auctionId } = useParams({ from: ROUTE });
  const { editable, reason } = useStoreEditable({ permission: 'auctions.write' });
  const [dialog, setDialog] = useState<'edit' | 'cancel' | 'end' | null>(null);
  // Prefetched by the route loader; polls while live.
  const query = useAuction(store.id, auctionId);
  const cancel = useCancelAuction(store.id);
  const auction = query.data;
  if (!auction) {
    return query.error ? (
      <PageContent>
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      </PageContent>
    ) : null;
  }

  const closeDialog = (open: boolean) => {
    if (!open) setDialog(null);
  };

  return (
    <PageContent>
      <PageHeader
        title={auction.lot.title}
        meta={
          <>
            <AuctionStatusBadge status={auction.status} />
            {auction.outcome ? <AuctionOutcomeBadge outcome={auction.outcome} /> : null}
          </>
        }
        description={
          <span title={format.utc(auction.createdAt)}>
            {auction.sale ? (
              <>
                <Link
                  to="/s/$storeId/auctions/sales/$saleId"
                  params={{ storeId: store.id, saleId: auction.sale.id }}
                  className="hover:underline"
                >
                  {t('sales.lotOf', { number: auction.sale.lotNumber, sale: auction.sale.title })}
                </Link>
                {' · '}
              </>
            ) : null}
            {[
              auction.lot.variantTitle,
              t('createdAt', { when: format.relative(auction.createdAt) }),
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
        }
        backTo={{ to: '/s/$storeId/auctions', params: { storeId: store.id } }}
        backLabel={t('title')}
        tour="auctions.detail.header"
        actions={
          editable ? (
            <Can permission="auctions.write">
              {auction.status === 'live' ? (
                <Button
                  variant="outline"
                  onClick={() => setDialog('end')}
                  data-tour="auctions.detail.end"
                >
                  <Flag aria-hidden />
                  {t('actions.end')}
                </Button>
              ) : null}
              {canCancel(auction) ? (
                <Button
                  variant="outline"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setDialog('cancel')}
                  data-tour="auctions.detail.cancel"
                >
                  <Ban aria-hidden />
                  {t('actions.cancel')}
                </Button>
              ) : null}
            </Can>
          ) : null
        }
      />
      {reason === 'closed' ? <ReadOnlyBanner reason="closed" /> : null}
      <div className="flex flex-col gap-6">
        <AuctionSummary auction={auction} />
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="flex flex-col gap-6 lg:col-span-2">
            <AuctionBidsCard storeId={store.id} auction={auction} canWrite={editable} />
          </div>
          <div className="flex flex-col gap-6">
            <AuctionOutcomeCard storeId={store.id} auction={auction} />
            {auction.status === 'ended' ? (
              <SecondChanceCard storeId={store.id} auction={auction} canWrite={editable} />
            ) : null}
            <AuctionLotCard storeId={store.id} auction={auction} />
            <AuctionSettingsCard
              auction={auction}
              canEdit={editable && isEditable(auction)}
              onEdit={() => setDialog('edit')}
            />
          </div>
        </div>
      </div>
      {editable ? (
        <>
          {isEditable(auction) ? (
            <AuctionFormDialog
              key={auction.version}
              storeId={store.id}
              currency={auction.currency}
              timeZone={displayTimeZone(store.timezone)}
              open={dialog === 'edit'}
              onOpenChange={closeDialog}
              auction={auction}
            />
          ) : null}
          {auction.status === 'live' ? (
            <EndAuctionDialog
              storeId={store.id}
              auction={auction}
              open={dialog === 'end'}
              onOpenChange={closeDialog}
            />
          ) : null}
          <ReasonDialog
            open={dialog === 'cancel'}
            onOpenChange={closeDialog}
            title={t('cancel.title')}
            description={
              auction.bidCount > 0
                ? t('cancel.withBids', { count: auction.bidCount })
                : t('cancel.description')
            }
            label={t('cancel.reason')}
            hint={t('cancel.hint')}
            confirmLabel={t('cancel.confirm')}
            pending={cancel.isPending}
            onConfirm={(text) => cancel.mutateAsync({ id: auction.id, reason: text })}
          />
        </>
      ) : null}
    </PageContent>
  );
}

export function AuctionDetailPage() {
  return (
    <FeatureErrorBoundary name="auctions.detail">
      <AuctionDetail />
    </FeatureErrorBoundary>
  );
}
